import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { SearchableSupplementsLegalEntityFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBulkCreateContractsTaxonomies } from 'modules/payroll_bundle/hooks/useBulkCreateContractsTaxonomies'

type LegalEntity = SearchableSupplementsLegalEntityFragment

interface TaxonomyFormValues {
  name: string
  legal_entity_ids: number[]
}

interface Props {
  legalEntities: LegalEntity[]
  setModalOpen: (value: boolean) => void
}

const TaxonomyForm = ({ legalEntities, setModalOpen }: Props) => {
  const { BulkCreateContractsTaxonomy } = useBulkCreateContractsTaxonomies()
  const taxonomyForm = useForm<TaxonomyFormValues>({
    defaultValues: {
      name: '',
      legal_entity_ids:
        legalEntities.length === 1 && typeof legalEntities[0]?.id === 'number'
          ? [legalEntities[0].id]
          : [],
    },
    onSubmit: async (data) => {
      const legalEntityIds = data.legal_entity_ids.map(Number)
      const result = await BulkCreateContractsTaxonomy({
        name: data.name,
        legalEntityIds: legalEntityIds,
      })
      const handler = handleResult(result, () => null)

      if (handler.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('handoffs.supplements.create_taxonomy.success_message'),
        })
        setModalOpen(false)
        taxonomyForm.resetForm()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('handoffs.supplements.create_taxonomy.error_message'),
        })
      }
      return handler
    },
  })

  const canSubmitTaxonomyForm =
    taxonomyForm.values.name !== '' && taxonomyForm.values.legal_entity_ids.length > 0

  return (
    <Modal
      title={i18n.t('handoffs.supplements.create_taxonomy.create_taxonomy_modal.title')}
      illustration={'add-block'}
      onClose={() => setModalOpen(false)}
      footer={
        <Button
          type="primary"
          onClick={() => taxonomyForm.handleSubmit()}
          disabled={!canSubmitTaxonomyForm || taxonomyForm.isSubmitting}
        >
          {i18n.t('common.save')}
        </Button>
      }
    >
      <Box gap="s24">
        <Box gap="s24">
          <Selector.Popover
            allSelectedLabel={
              legalEntities.length === 1
                ? legalEntities[0]?.legalName
                : i18n.t('handoffs.supplements.create_taxonomy.create_taxonomy_modal.all')
            }
            mandatory
            headerLabel={i18n.t(
              'handoffs.supplements.create_taxonomy.create_taxonomy_modal.header'
            )}
            placeholder={i18n.t(
              'handoffs.supplements.create_taxonomy.create_taxonomy_modal.placeholder'
            )}
            blankLabel={i18n.t('common.empty')}
            value={taxonomyForm.values.legal_entity_ids}
            isMultiSelect
            onChange={(selection) => {
              taxonomyForm.setValues({
                ...taxonomyForm.values,
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                legal_entity_ids: selection as number[],
              })
            }}
            label={i18n.t(
              'handoffs.supplements.create_taxonomy.create_taxonomy_modal.legal_entity'
            )}
          >
            {legalEntities.map((legalEntity) => (
              <Selector.Option
                key={legalEntity.id}
                value={legalEntity.id}
                label={legalEntity.legalName || ''}
                searchValue={legalEntity.legalName || ''}
              >
                {legalEntity.legalName}
              </Selector.Option>
            ))}
          </Selector.Popover>
          <TextField
            value={taxonomyForm.values.name}
            label={i18n.t(
              'handoffs.supplements.create_taxonomy.create_taxonomy_modal.category_name'
            )}
            onChange={(value) => {
              taxonomyForm.setValues({ ...taxonomyForm.values, name: value })
            }}
            mandatory
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(TaxonomyForm)
